<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-primary" @click="handleCreateModule" :disabled="loading">
          <i class="ri-add-line"></i>
          新增模組
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="card">
      <div class="card-body">
        <div v-if="loading" class="loading-inline">載入模組中...</div>
        <div v-else class="table-wrapper">
          <table class="module-table">
            <thead>
              <tr>
                <th>模組名稱</th>
                <th>啟用日期起</th>
                <th>啟用日期迄</th>
                <th>啟用狀態</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="module in modules" :key="module.id">
                <td>{{ module.moduleName }}</td>
                <td>{{ formatModuleDate(module.activeStartAt) }}</td>
                <td>{{ module.activeEndAt ? formatModuleDate(module.activeEndAt) : '無限制' }}</td>
                <td>
                  <span class="badge" :class="getModuleStatusBadgeClass(module.effectiveStatus)">
                    {{ getModuleStatusText(module.effectiveStatus) }}
                  </span>
                </td>
                <td>
                  <div class="action-buttons">
                    <router-link
                      :to="{ name: 'BirthdayGiftConfigEdit', params: { moduleId: module.id } }"
                      class="btn btn-sm btn-primary"
                    >
                      <i class="ri-edit-line"></i>
                      編輯
                    </router-link>
                    <button
                      type="button"
                      class="btn btn-sm btn-danger"
                      :disabled="modules.length <= 1"
                      @click="handleDeleteModule(module)"
                    >
                      <i class="ri-delete-bin-line"></i>
                      刪除
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="createModuleLightboxOpen"
        class="create-module-lightbox-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-module-lightbox-title"
        @click.self="closeCreateModuleLightbox"
      >
        <div class="create-module-lightbox">
          <h3 id="create-module-lightbox-title" class="create-module-lightbox-title">新增模組</h3>
          <p class="create-module-lightbox-text">請輸入新模組名稱，建立後將複製第一個模組的設定作為初始內容。</p>
          <div class="form-group">
            <label class="form-label" for="create-module-name">模組名稱</label>
            <input
              id="create-module-name"
              ref="createModuleNameInput"
              v-model="newModuleName"
              type="text"
              class="form-control"
              maxlength="100"
              placeholder="例如：2026 生日禮"
              :disabled="createModuleSubmitting"
              @keydown.enter.prevent="confirmCreateModule"
            />
            <span v-if="createModuleError" class="error-text">{{ createModuleError }}</span>
          </div>
          <div class="create-module-lightbox-actions">
            <button
              type="button"
              class="btn btn-secondary"
              :disabled="createModuleSubmitting"
              @click="closeCreateModuleLightbox"
            >
              取消
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="createModuleSubmitting"
              @click="confirmCreateModule"
            >
              <span v-if="createModuleSubmitting"><i class="ri-loader-4-line spin"></i> 建立中...</span>
              <span v-else>建立</span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { autoSendService } from '@/api/modules/autoSend.js';
import { showSuccess, showError } from '@/utils/swal.js';

export default {
  name: 'BirthdayGiftModuleList',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('生日禮模組清單');
    const loading = ref(false);
    const modules = ref([]);
    const createModuleLightboxOpen = ref(false);
    const newModuleName = ref('新生日禮模組');
    const createModuleError = ref('');
    const createModuleSubmitting = ref(false);
    const createModuleNameInput = ref(null);

    const loadModules = async () => {
      loading.value = true;
      try {
        const response = await autoSendService.getBirthdayGiftModules();
        if (response?.success && Array.isArray(response.data)) {
          modules.value = response.data;
        } else {
          modules.value = [];
        }
      } catch (error) {
        showError('載入模組清單失敗', error.message || '請稍後再試');
      } finally {
        loading.value = false;
      }
    };

    const goEdit = (moduleId) => {
      router.push({ name: 'BirthdayGiftConfigEdit', params: { moduleId } });
    };

    const resetCreateModuleLightbox = () => {
      createModuleLightboxOpen.value = false;
      newModuleName.value = '新生日禮模組';
      createModuleError.value = '';
    };

    const closeCreateModuleLightbox = () => {
      if (createModuleSubmitting.value) {
        return;
      }
      resetCreateModuleLightbox();
    };

    const handleCreateModule = async () => {
      createModuleError.value = '';
      newModuleName.value = '新生日禮模組';
      createModuleLightboxOpen.value = true;
      await nextTick();
      createModuleNameInput.value?.focus();
      createModuleNameInput.value?.select();
    };

    const confirmCreateModule = async () => {
      const moduleName = newModuleName.value?.trim();
      if (!moduleName) {
        createModuleError.value = '請輸入模組名稱';
        createModuleNameInput.value?.focus();
        return;
      }

      createModuleError.value = '';
      createModuleSubmitting.value = true;
      try {
        const response = await autoSendService.createBirthdayGiftModule({
          moduleName,
          copyFromModuleId: modules.value[0]?.id || undefined,
          isEnabled: false
        });

        if (response?.success && response.data?.id) {
          resetCreateModuleLightbox();
          await showSuccess('模組建立成功', '已建立新模組，請進入編輯頁設定內容');
          goEdit(response.data.id);
          return;
        }
        createModuleError.value = response?.message || '建立失敗，請稍後再試';
      } catch (error) {
        createModuleError.value = error.message || '建立失敗，請稍後再試';
      } finally {
        createModuleSubmitting.value = false;
      }
    };

    const handleDeleteModule = async (module) => {
      if (modules.value.length <= 1) {
        showError('無法刪除', '至少需保留一個模組');
        return;
      }

      const confirmed = window.confirm(`確定要刪除模組「${module.moduleName}」嗎？此操作無法復原。`);
      if (!confirmed) {
        return;
      }

      try {
        loading.value = true;
        const response = await autoSendService.deleteBirthdayGiftModule(module.id);
        if (response?.success) {
          await showSuccess('模組已刪除', '模組已成功刪除');
          await loadModules();
          return;
        }
        showError('刪除模組失敗', response?.message || '未知錯誤');
      } catch (error) {
        showError('刪除模組失敗', error.message || '請稍後再試');
      } finally {
        loading.value = false;
      }
    };

    const formatModuleDate = (isoString) => {
      if (!isoString) return '-';
      return new Date(isoString).toLocaleDateString('zh-TW', {
        timeZone: 'Asia/Taipei',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    };

    const getModuleStatusText = (status) => {
      const map = {
        disabled: '已停用',
        scheduled: '尚未生效',
        active: '生效中',
        expired: '已過期'
      };
      return map[status] || status || '-';
    };

    const getModuleStatusBadgeClass = (status) => {
      const map = {
        disabled: 'badge-secondary',
        scheduled: 'badge-warning',
        active: 'badge-success',
        expired: 'badge-danger'
      };
      return map[status] || 'badge-secondary';
    };

    onMounted(loadModules);

    return {
      pageTitle,
      loading,
      modules,
      createModuleLightboxOpen,
      newModuleName,
      createModuleError,
      createModuleSubmitting,
      createModuleNameInput,
      handleCreateModule,
      closeCreateModuleLightbox,
      confirmCreateModule,
      handleDeleteModule,
      goEdit,
      formatModuleDate,
      getModuleStatusText,
      getModuleStatusBadgeClass
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.module-table {
  width: 100%;
  min-width: 720px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 14px;
}

.loading-inline {
  color: #6b7280;
  font-size: 14px;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.create-module-lightbox-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.45);
}

.create-module-lightbox {
  width: 100%;
  max-width: 440px;
  padding: 22px 24px 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);
}

.create-module-lightbox-title {
  margin: 0 0 8px;
  font-size: 17px;
  font-weight: 600;
  color: #1e293b;
}

.create-module-lightbox-text {
  margin: 0 0 16px;
  font-size: 14px;
  color: #475569;
  line-height: 1.55;
}

.create-module-lightbox .form-group {
  margin-bottom: 20px;
}

.create-module-lightbox .form-label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.create-module-lightbox .error-text {
  display: block;
  margin-top: 6px;
  font-size: 13px;
  color: #dc3545;
}

.create-module-lightbox-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}
</style>
